// Wie ein Kontakt zustande gekommen ist — Vokabular und Umformung.
//
// Die Frage stellt die Kontakt-Anlage („Wie kommt der Kontakt zustande?"), die
// Antwort steht seit 09/2026 als `Contact.kontaktherkunft` in der Datenbank und
// wird in der Kontaktakte wieder angezeigt. Beide Seiten müssen dasselbe
// Vokabular und dieselbe Schreibweise benutzen — sonst zeigt die Akte eine
// Auswahl, die es in der Anlage nicht gibt, oder liest ihre eigenen Werte nicht
// zurück. Deshalb steht beides hier und nicht in einer der Masken.
//
// Gespeichert wird als „a; b" — dasselbe Format wie `unternehmensfunktion` und
// `memberships`, für die es in der Datenbank ebenfalls keine Zwischentabelle
// gibt.

/** Der Wert, an dem der Freitext hängt. */
export const HERKUNFT_SONSTIGES = "Sonstiges";

/** Trennzeichen der gespeicherten Liste — wie bei den übrigen Mehrfachfeldern. */
export const HERKUNFT_TRENNER = "; ";

/**
 * Die Herkünfte zur Auswahl.
 *
 * „Netzwerk" ist als Herkunft entfallen (PO-Notiz 02.08.2026): Die Angabe kam
 * über den Empfehlungsgeber oder die Verknüpfung ohnehin präziser herein.
 * Bestehende Kontakte behalten den Wert — `herkunftZerlegen` gibt ihn zurück,
 * er steht nur nicht mehr zur Wahl.
 */
export const HERKUNFT_BASIS_OPTIONEN: ReadonlyArray<{ value: string; label: string }> = [
  { value: "Eigenakquise", label: "Eigenakquise" },
  { value: "Empfehlungsgeber", label: "Empfehlungsgeber" },
  { value: "Tippgeber", label: "Tippgeber/Vermittler" },
];

/**
 * Die Auswahl der ANLAGE (Teil C, 23.09.2026): ohne „Verknüpfung zu
 * bestehendem Kontakt" — wer den Kontakt gebracht hat, wird jetzt als
 * Empfehlungsgeber oder Tippgeber verknüpft. Die Akte führt den Wert weiter
 * (`HERKUNFT_OPTIONEN`), damit der Bestand lesbar bleibt.
 */
export const HERKUNFT_ANLAGE_OPTIONEN: ReadonlyArray<{ value: string; label: string }> = [
  { value: "Eigenakquise", label: "Eigenakquise" },
  { value: "Empfehlungsgeber", label: "Empfehlungsgeber" },
  { value: "Tippgeber", label: "Tippgeber/Vermittler" },
  { value: HERKUNFT_SONSTIGES, label: "Sonstiges" },
];

/** Basis plus „Sonstiges" — die vollständige Auswahl. */
export const HERKUNFT_OPTIONEN: ReadonlyArray<{ value: string; label: string }> = [
  ...HERKUNFT_BASIS_OPTIONEN,
  { value: HERKUNFT_SONSTIGES, label: "Sonstiges" },
];

/** Auswahl → gespeicherter Wert. Leere Auswahl ergibt "" (Spalte bleibt leer). */
export function herkunftZusammensetzen(auswahl: readonly string[]): string {
  return auswahl
    .map((v) => v.trim())
    .filter(Boolean)
    .join(HERKUNFT_TRENNER);
}

/**
 * Gespeicherter Wert → Auswahl.
 *
 * Trennt an „;" statt an „; ", damit ein von Hand gesetzter Wert ohne
 * Leerzeichen nicht als eine einzige, unbekannte Herkunft durchgeht. Unbekannte
 * Werte bleiben erhalten — der Altwert „Netzwerk" ginge sonst beim ersten
 * Speichern der Akte verloren, obwohl niemand ihn angefasst hat.
 */
export function herkunftZerlegen(gespeichert: string | null | undefined): string[] {
  return String(gespeichert ?? "")
    .split(";")
    .map((v) => v.trim())
    .filter(Boolean);
}

/**
 * Der Freitext gehört zu „Sonstiges" — steht die Herkunft nicht (mehr) in der
 * Auswahl, ist er gegenstandslos und geht als "" mit. Ohne diese Regel bliebe
 * ein abgewählter Freitext in der Datenbank stehen und tauchte beim nächsten
 * Anhaken unerwartet wieder auf.
 */
export function herkunftFreitextFuerSpeichern(
  auswahl: readonly string[],
  freitext: string | null | undefined,
): string {
  return auswahl.includes(HERKUNFT_SONSTIGES) ? String(freitext ?? "").trim() : "";
}

/**
 * Je Herkunft: wann und wie der Kontakt zustande gekommen ist (User
 * 27.09.2026). Im Formular und in `Contact.kontaktherkunftDetails` als
 * JSON-Text `{ "<Herkunft>": { wann, wie } }`.
 */
export type HerkunftDetail = { wann?: string; wie?: string };
export type HerkunftDetails = Record<string, HerkunftDetail>;

/** Formular-/Spaltenwert → Objekt. Kaputter oder leerer Text ergibt {}. */
export function herkunftDetailsLesen(wert: unknown): HerkunftDetails {
  if (typeof wert !== "string" || !wert.trim()) return {};
  try {
    const o = JSON.parse(wert);
    return o && typeof o === "object" && !Array.isArray(o) ? (o as HerkunftDetails) : {};
  } catch {
    return {};
  }
}

/**
 * Nur, was zu einer gewählten Herkunft gehört und nicht leer ist — ein
 * abgewählter Eintrag geht wie der Sonstiges-Freitext nicht mit. Ergibt "",
 * wenn nichts übrig bleibt.
 */
export function herkunftDetailsFuerSpeichern(
  auswahl: readonly string[],
  details: HerkunftDetails,
): string {
  const out: HerkunftDetails = {};
  for (const h of auswahl) {
    const wann = (details[h]?.wann ?? "").trim();
    const wie = (details[h]?.wie ?? "").trim();
    if (wann || wie) out[h] = { ...(wann ? { wann } : {}), ...(wie ? { wie } : {}) };
  }
  return Object.keys(out).length ? JSON.stringify(out) : "";
}
